import { Injectable } from '@angular/core';
import { EntityApiService } from './entity-api.service';
import {
  OrganizationMembership,
  OrganizationMembershipModel,
  CreateMembership
} from '../models/organization-membership.model';

/**
 * Service for managing organization memberships (adding/removing/updating members).
 * 
 * All operations are scoped to a specific organization and require an organization ID.
 * Only users with admin role can manage memberships.
 * 
 * Routes: /organizations/:orgId/members
 * 
 * @example
 * ```typescript
 * // List members
 * membershipService.list(orgId).subscribe(members => ...);
 * 
 * // Add a member by email
 * membershipService.create(orgId, { email: 'john@example.com', role: 'editor' }).subscribe();
 * 
 * // Update member role (using user ID)
 * membershipService.update({ id: userId, role: 'admin' }).subscribe();
 * 
 * // Remove member (using user ID)
 * membershipService.delete(userId).subscribe();
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class OrganizationMembershipService extends EntityApiService<
  OrganizationMembership,
  OrganizationMembershipModel,
  CreateMembership
> {
  constructor() {
    // This service is scoped to organizations (parent)
    // Routes: /organizations/:orgId/members
    super(OrganizationMembershipModel, 'members', 'organizations');
  }

  // All CRUD operations inherited from EntityApiService:
  // - list(orgId, options?) - List members for an organization
  // - get(userId) - Get single membership by user ID
  // - create(orgId, data) - Add member to organization (by email)
  // - update(data) - Update member role (id is user ID)
  // - delete(userId) - Remove member from organization (by user ID)
}
